import { trackClassicClicked, trackResultShared } from "@/daily/analytics";
import { useState } from "react";
import { Link } from "react-router-dom";
import { dt } from "@/daily/strings";
import {
  buildShareText,
  formatCountdown,
  formatDuration,
  formatPuzzleDate,
  hasWon,
  scoreLabel,
} from "@/daily/game";
import { useMediaQuery, useNextPuzzleCountdown } from "@/daily/hooks";
import { DailyGuess, DailyPuzzle } from "@/daily/types";
import { FitText } from "./FitText";
import {
  ArrowIcon,
  CheckIcon,
  CopyIcon,
  CrossIcon,
  PlaneIcon,
  ShareIcon,
} from "./icons";

// Wide enough for the boarding pass to lie on its side. Keep in step with
// the matching media query in daily.css.
const LANDSCAPE_PASS_QUERY = "(min-width: 1000px)";

// When the result squares start flipping in, and the gap between them (ms).
const SQUARES_START = 1000;
const SQUARE_STAGGER = 240;

// Touch-first devices: phones and tablets, whatever their width.
const TOUCH_DEVICE_QUERY = "(pointer: coarse)";

interface EndScreenProps {
  puzzle: DailyPuzzle;
  guesses: DailyGuess[];
}

// The full-page screen a finished game goes straight to. Everything about
// the flight sits on a boarding pass, with the result on its tear-off stub;
// below it, the share button and the countdown to the next flight.
// Gold for a win, dark for a miss.
export const EndScreen = ({ puzzle, guesses }: EndScreenProps) => {
  const won = hasWon(guesses);
  const countdown = useNextPuzzleCountdown();
  const landscape = useMediaQuery(LANDSCAPE_PASS_QUERY);
  // Phones and tablets open the system share sheet; desktops copy.
  const touchDevice = useMediaQuery(TOUCH_DEVICE_QUERY);
  const useShareSheet = touchDevice && "share" in navigator;
  const [copied, setCopied] = useState(false);
  // The share button waits for the last square to land before it pops in.
  const afterSquares = SQUARES_START + guesses.length * SQUARE_STAGGER;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText(puzzle, guesses));
      trackResultShared(puzzle, guesses, "copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: nothing more we can do here.
    }
  };

  const share = async () => {
    try {
      await navigator.share({ text: buildShareText(puzzle, guesses) });
      trackResultShared(puzzle, guesses, "share_sheet");
    } catch (error) {
      // Closing the share sheet is not a failure; anything else falls back
      // to copying.
      if (error instanceof DOMException && error.name === "AbortError") return;
      await copy();
    }
  };

  return (
    <div className={`ds ds-end ${won ? "ds-end--won" : "ds-end--lost"}`}>
      {won && (
        <svg
          className="ds-end__burst"
          viewBox="0 0 400 400"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          {/* Rays all the way round, so the burst can turn slowly. */}
          <g className="ds-end__rays">
          {Array.from({ length: 13 }, (_, index) => {
            const start = (index * 2 * Math.PI) / 13;
            const end = start + Math.PI / 13;
            const point = (angle: number) =>
              `${200 + 600 * Math.cos(angle)},${200 + 600 * Math.sin(angle)}`;
            return (
              <polygon
                key={index}
                points={`200,200 ${point(start)} ${point(end)}`}
                fill="#EDCB78"
              />
            );
          })}
          </g>
        </svg>
      )}

      <div className="ds-end__column">
        <div className="ds-pass">
          <div className="ds-pass__main">
            <div className="ds-pass__band">
              <span className="ds-pass__date">
                Daily Service · {formatPuzzleDate(puzzle.date)}
              </span>
              <span className="ds-pass__outcome">
                {won ? <CheckIcon size={24} /> : <CrossIcon size={24} width={2.8} />}
                {won ? dt("won") : dt("lost")}
              </span>
            </div>

            <div className="ds-pass__body">
              <div className="ds-pass__answer">
                <span className="ds-eyebrow ds-eyebrow--red">
                  {won ? dt("landedIn") : dt("answerWas")}
                </span>
                <h1 className="ds-pass__city">
                  <FitText max={landscape ? 104 : 60} min={32}>
                    {puzzle.destination.city}
                  </FitText>
                </h1>
              </div>

              <div className="ds-pass__route">
                <span className="ds-pass__end">
                  <span className="ds-pass__code">{puzzle.origin.iata}</span>
                  <span className="ds-pass__name">{puzzle.origin.city}</span>
                </span>
                <span className="ds-pass__dots" />
                <span className="ds-pass__badge">
                  <PlaneIcon size={14} />
                </span>
                <span className="ds-pass__dots" />
                <span className="ds-pass__end ds-pass__end--to">
                  <span className="ds-pass__code">{puzzle.destination.iata}</span>
                  <span className="ds-pass__name">{puzzle.destination.city}</span>
                </span>
              </div>

              <dl className="ds-pass__facts">
                <div>
                  <dt className="ds-label">{dt("flight")}</dt>
                  <dd>{puzzle.flightNumber}</dd>
                </div>
                <div>
                  <dt className="ds-label">{dt("duration")}</dt>
                  <dd>{formatDuration(puzzle.durationMinutes)}</dd>
                </div>
                <div>
                  <dt className="ds-label">{puzzle.aircraftMaker}</dt>
                  <dd>{puzzle.aircraftModel}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="ds-pass__stub">
            <span className="ds-pass__result">
              <span className="ds-pass__result-label">{dt("result")}</span>
              <span className="ds-pass__score">{scoreLabel(guesses)}</span>
            </span>
            <span
              className="ds-squares"
              aria-label={dt("yourGuesses", {
                list: guesses
                  .map((guess) =>
                    guess.band === "correct"
                      ? dt("guessCorrect")
                      : guess.band === "near"
                      ? dt("guessClose")
                      : dt("guessMiss")
                  )
                  .join(", "),
              })}
            >
              {guesses.map((guess, index) => (
                <span
                  key={index}
                  className={`ds-square ds-square--${guess.band}`}
                  // The squares flip in one at a time, like a row of tiles.
                  style={{ animationDelay: `${SQUARES_START + index * SQUARE_STAGGER}ms` }}
                >
                  {guess.band === "correct" ? (
                    <PlaneIcon size={20} />
                  ) : (
                    <CrossIcon size={16} width={2.4} />
                  )}
                </span>
              ))}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="ds-end__share"
          onClick={useShareSheet ? share : copy}
          style={{ animationDelay: `${afterSquares + 200}ms` }}
        >
          {useShareSheet ? <ShareIcon size={20} /> : <CopyIcon size={20} />}
          {copied ? dt("copied") : useShareSheet ? dt("share") : dt("copy")}
        </button>

        <div className="ds-end__next" style={{ animationDelay: `${afterSquares + 500}ms` }}>
          <span className="ds-end__next-label">{dt("next")}</span>
          <span className="ds-end__countdown">{formatCountdown(countdown)}</span>
        </div>

        {/* A quieter second action: on to the main Flight Guesser game. */}
        <Link
          to="/"
          onClick={() => trackClassicClicked(puzzle, guesses)}
          className="ds-end__classic"
          style={{ animationDelay: `${afterSquares + 800}ms` }}
        >
          {dt("classic")}
          <ArrowIcon size={18} />
        </Link>
      </div>
    </div>
  );
};
